import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, ToggleButtonGroup, ToggleButton, Chip, TextField } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useChowaTheme } from '../config/chowaThemeContext';
import { getChowaUnits, deleteChowaUnit } from '../api_config';

// Google Fonts から BIZ UDPGothic を直接読み込み
const fontFaceStyle = `
  @font-face {
    font-family: 'BIZ UDPGothic';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/bizudpgothic/v14/hES36X5pHAIBjmS84VL0Bue83nU.ttf) format('truetype');
  }
`;

// スタイルを head に追加（一度だけ）
if (typeof document !== 'undefined' && !document.querySelector('#biz-udp-gothic-font')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'biz-udp-gothic-font';
  styleElement.textContent = fontFaceStyle;
  document.head.appendChild(styleElement);
}

const EditUnits = ({ onBack, onEdit }) => {
  const { getCurrentTheme } = useChowaTheme();
  const currentTheme = getCurrentTheme();
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeries, setSelectedSeries] = useState('all');
  const [selectedExhaustGas, setSelectedExhaustGas] = useState('all');
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [availableSeries, setAvailableSeries] = useState([]);
  const [availableExhaustGases, setAvailableExhaustGases] = useState([]);

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    filterAndSortUnits();
  }, [units, selectedSeries, selectedExhaustGas, deviceSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const response = await getChowaUnits();
      if (response.data.status === 'success') {
        const unitsData = response.data.data.units;
        setUnits(unitsData);
        
        // 利用可能なシリーズを抽出
        const series = [...new Set(unitsData.map(unit => unit.model_series).filter(Boolean))].sort();
        setAvailableSeries(series);
        
        // 利用可能な排ガス規制を抽出
        const exhaustGases = [...new Set(unitsData.map(unit => unit.exhaust_gas).filter(Boolean))].sort();
        setAvailableExhaustGases(exhaustGases);
      } else {
        setError('ユニットデータの取得に失敗しました');
      }
    } catch (err) {
      console.error('ユニット取得エラー:', err);
      setError('ユニットデータの取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortUnits = () => {
    let filtered = units;
    
    // シリーズでフィルタリング
    if (selectedSeries !== 'all') {
      filtered = filtered.filter(unit => unit.model_series === selectedSeries);
    }
    
    // 排ガス規制でフィルタリング
    if (selectedExhaustGas !== 'all') {
      filtered = filtered.filter(unit => unit.exhaust_gas === selectedExhaustGas);
    }
    
    // デバイスIDで検索
    if (deviceSearchQuery.trim()) {
      filtered = filtered.filter(unit => 
        unit.device && unit.device.toLowerCase().includes(deviceSearchQuery.toLowerCase())
      );
    }
    
    setFilteredUnits(filtered);
  };

  const handleSeriesChange = (event, newSeries) => {
    if (newSeries !== null) {
      setSelectedSeries(newSeries);
    }
  };

  const handleExhaustGasChange = (event, newExhaustGas) => {
    if (newExhaustGas !== null) {
      setSelectedExhaustGas(newExhaustGas);
    }
  };

  const handleDeviceSearchChange = (event) => {
    setDeviceSearchQuery(event.target.value);
  };

  const clearDeviceSearch = () => {
    setDeviceSearchQuery('');
  };



  const handleDelete = async (unitId, customData) => {
    if (window.confirm(`ユニット "${customData}" を削除しますか？`)) {
      try {
        const response = await deleteChowaUnit(unitId);
        if (response.data.status === 'success') {
          // 削除成功後、一覧を再取得
          fetchUnits();
        } else {
          alert('削除に失敗しました');
        }
      } catch (err) {
        console.error('削除エラー:', err);
        alert('削除中にエラーが発生しました');
      }
    }
  };

  const handleEdit = (unit) => {
    onEdit(unit);
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            ユニット編集
          </Typography>
          <Button 
            variant="outlined" 
            onClick={onBack}
            sx={{ color: currentTheme.primary, borderColor: currentTheme.primary }}
          >
            一覧に戻る
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            データを読み込み中...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            ユニット編集
          </Typography>
          <Button 
            variant="outlined" 
            onClick={onBack}
            sx={{ color: currentTheme.primary, borderColor: currentTheme.primary }}
          >
            一覧に戻る
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={fetchUnits}
            sx={{ backgroundColor: currentTheme.primary }}
          >
            再試行
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button 
          variant="outlined" 
          onClick={onBack}
          sx={{ color: currentTheme.primary, borderColor: currentTheme.primary }}
        >
          一覧に戻る
        </Button>
      </Box>

      {/* フィルタリングとソート */}
      <Box sx={{ mb: 3 }}>
        {/* 機種シリーズで絞り込み */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            機種シリーズで絞り込み:
          </Typography>
          <ToggleButtonGroup
            value={selectedSeries}
            exclusive
            onChange={handleSeriesChange}
            size="small"
            sx={{ 
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                borderRadius: '20px',
                border: '1px solid',
                borderColor: 'divider',
                px: 2,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 500,
                                 '&.Mui-selected': {
                   backgroundColor: '#10b981', // 緑
                   color: 'white',
                   borderColor: '#10b981',
                   '&:hover': {
                     backgroundColor: '#10b981',
                   }
                 },
                                 '&:hover': {
                   backgroundColor: 'rgba(0,0,0,0.04)',
                   borderColor: '#10b981',
                 }
              }
            }}
          >
            <ToggleButton value="all">
              すべて
            </ToggleButton>
            {availableSeries.map((series) => (
              <ToggleButton key={series} value={series}>
                {series}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* 排ガス規制で絞り込み */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            排ガス規制で絞り込み:
          </Typography>
          <ToggleButtonGroup
            value={selectedExhaustGas}
            exclusive
            onChange={handleExhaustGasChange}
            size="small"
            sx={{ 
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                borderRadius: '20px',
                border: '1px solid',
                borderColor: 'divider',
                px: 2,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 500,
                                 '&.Mui-selected': {
                   backgroundColor: '#1e3a8a', // ネイビー
                   color: 'white',
                   borderColor: '#1e3a8a',
                   '&:hover': {
                     backgroundColor: '#1e3a8a',
                   }
                 },
                                 '&:hover': {
                   backgroundColor: 'rgba(0,0,0,0.04)',
                   borderColor: '#1e3a8a',
                 }
              }
            }}
          >
            <ToggleButton value="all">
              すべて
            </ToggleButton>
            {availableExhaustGases.map((exhaustGas) => (
              <ToggleButton key={exhaustGas} value={exhaustGas}>
                <span style={{ fontFamily: '"BIZ UDPGothic", "Noto Sans JP", sans-serif', fontFeatureSettings: '"jp90" 1' }}>{exhaustGas}</span>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* デバイスIDで検索 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            デバイスIDで検索:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="デバイスIDを入力..."
              value={deviceSearchQuery}
              onChange={handleDeviceSearchChange}
              sx={{
                minWidth: '250px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: currentTheme.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: currentTheme.primary,
                  }
                }
              }}
            />
            {deviceSearchQuery && (
              <Button
                size="small"
                onClick={clearDeviceSearch}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  borderRadius: '20px',
                  color: 'text.secondary',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: currentTheme.primary,
                    color: currentTheme.primary
                  }
                }}
              >
                クリア
              </Button>
            )}
          </Box>
        </Box>


      </Box>

      {/* 更新ボタン */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUnits}
          sx={{ 
            color: currentTheme.primary, 
            borderColor: currentTheme.primary,
            '&:hover': {
              borderColor: currentTheme.primary,
              backgroundColor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          更新
        </Button>
      </Box>
      
      {filteredUnits.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {(() => {
              if (deviceSearchQuery.trim()) {
                return `デバイスID「${deviceSearchQuery}」に一致するユニットがありません`;
              } else if (selectedSeries !== 'all') {
                return `シリーズ「${selectedSeries}」のユニットがありません`;
              } else if (selectedExhaustGas !== 'all') {
                return `排ガス規制「${selectedExhaustGas}」のユニットがありません`;
              } else {
                return 'ユニットがありません';
              }
            })()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {deviceSearchQuery.trim() ? '検索条件を変更するか、' : ''}新規ユニット登録からユニットを追加してください
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {filteredUnits.length}件のユニット
            </Typography>
            {(selectedSeries !== 'all' || selectedExhaustGas !== 'all' || deviceSearchQuery.trim()) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedSeries !== 'all' && (
                  <Chip 
                    label={`シリーズ: ${selectedSeries}`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    onDelete={() => setSelectedSeries('all')}
                  />
                )}
                {selectedExhaustGas !== 'all' && (
                  <Chip 
                    label={<>排ガス: <span style={{ fontFamily: '"BIZ UDPGothic", "Noto Sans JP", sans-serif', fontFeatureSettings: '"jp90" 1' }}>{selectedExhaustGas}</span></>} 
                    size="small" 
                    color="secondary" 
                    variant="outlined"
                    onDelete={() => setSelectedExhaustGas('all')}
                  />
                )}
                {deviceSearchQuery.trim() && (
                  <Chip 
                    label={`デバイスID: ${deviceSearchQuery}`} 
                    size="small" 
                    color="info" 
                    variant="outlined"
                    onDelete={clearDeviceSearch}
                  />
                )}
              </Box>
            )}
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Custom Data</TableCell>
                  <TableCell>機種シリーズ</TableCell>
                  <TableCell>機種号機</TableCell>
                  <TableCell>製造番号</TableCell>
                  <TableCell>排ガス規制</TableCell>
                  <TableCell>デバイスID</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUnits.map((unit) => (
                  <TableRow key={unit.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {unit.custom_data}
                      </Typography>
                    </TableCell>
                    <TableCell>{unit.model_series || '-'}</TableCell>
                    <TableCell>{unit.model_type || '-'}</TableCell>
                    <TableCell>{unit.unit_serial_number || '-'}</TableCell>
                    <TableCell><span style={{ fontFamily: '"BIZ UDPGothic", "Noto Sans JP", sans-serif', fontFeatureSettings: '"jp90" 1' }}>{unit.exhaust_gas || '-'}</span></TableCell>
                    <TableCell>{unit.device || '-'}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(unit)}
                        sx={{ color: currentTheme.primary }}
                        title="編集"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(unit.id, unit.custom_data)}
                        sx={{ color: 'error.main' }}
                        title="削除"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default EditUnits;
